import { supabase } from '@/integrations/supabase/client'
import { getDeviceLabel } from '@/lib/deviceLabel'

export interface AuthSessionRow {
  session_id: string
  device_label: string
  user_agent: string | null
  ip_address: string | null
  created_at: string
  last_active_at: string
  is_current: boolean
}

export interface SessionAlertRow {
  id: string
  session_id: string
  device_label: string
  ip_address: string | null
  created_at: string
}

const REGISTERED_KEY = 'fluity_auth_session_registered'

function currentSessionStorageKey(sessionId: string): string {
  return `${REGISTERED_KEY}:${sessionId}`
}

const SESSION_VALIDATION_TIMEOUT_MS = 8_000

/** Retorna false quando o JWT tem session_id revogado/inválido (RLS bloqueia dados). */
export async function isCurrentAuthSessionValid(): Promise<boolean | null> {
  try {
    const result = await Promise.race([
      supabase.rpc('is_auth_session_valid'),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Timeout ao validar sessão')), SESSION_VALIDATION_TIMEOUT_MS)
      ),
    ])
    const { data, error } = result
    if (error) {
      console.warn('[authSessions] Falha ao validar sessão:', error.message)
      return null
    }
    return data === true
  } catch (err) {
    console.warn('[authSessions] Validação de sessão não respondeu a tempo:', err)
    return null
  }
}

/** Registra metadados da sessão atual (idempotente por session_id). */
export async function registerCurrentAuthSession(): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) return

  let sessionId: string | null = null
  try {
    const payload = JSON.parse(atob(session.access_token.split('.')[1] ?? ''))
    sessionId = typeof payload.session_id === 'string' ? payload.session_id : null
  } catch {
    return
  }
  if (!sessionId) return

  const storageKey = currentSessionStorageKey(sessionId)
  if (sessionStorage.getItem(storageKey) === '1') return

  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : null
  const { error } = await supabase.rpc('register_auth_session', {
    p_device_label: getDeviceLabel(userAgent ?? undefined),
    p_user_agent: userAgent,
    p_ip_address: null,
  })

  if (!error) {
    sessionStorage.setItem(storageKey, '1')
  }
}

export async function listMyAuthSessions(): Promise<AuthSessionRow[]> {
  const { data, error } = await supabase.rpc('list_my_auth_sessions')
  if (error) throw error
  return (data ?? []) as AuthSessionRow[]
}

export async function revokeAuthSession(sessionId: string): Promise<boolean> {
  const { data, error } = await supabase.rpc('revoke_my_auth_session', {
    p_session_id: sessionId,
  })
  if (error) throw error
  return Boolean(data)
}

export async function revokeOtherAuthSessions(): Promise<number> {
  const { data, error } = await supabase.rpc('revoke_other_auth_sessions')
  if (error) throw error
  return typeof data === 'number' ? data : 0
}

export async function getPendingSessionAlerts(): Promise<SessionAlertRow[]> {
  const { data, error } = await supabase.rpc('get_pending_session_alerts')
  if (error) throw error
  return (data ?? []) as SessionAlertRow[]
}

export async function dismissSessionAlerts(): Promise<void> {
  const { error } = await supabase.rpc('dismiss_session_alerts')
  if (error) throw error
}

export async function getMaxConcurrentSessions(): Promise<number> {
  const { data, error } = await supabase.rpc('get_max_concurrent_sessions')
  if (error) throw error
  return typeof data === 'number' ? data : 10
}

export async function updateMaxConcurrentSessions(max: number): Promise<number> {
  const { data, error } = await supabase.rpc('update_max_concurrent_sessions', {
    p_max: max,
  })
  if (error) throw error
  return typeof data === 'number' ? data : max
}
